import React, { useState } from "react";
import { Button } from "../components/Button/Button";
import { Card } from "../components/Card/Card";
import { Logo } from "../components/Logo/Logo";
import "./ResetPassword.css";
import { config } from "../config";

interface ResetFormState {
  email: string;
}

type ResetErrors = Partial<Record<keyof ResetFormState, string>>;

const initial_form_state: ResetFormState = {
  email: "",
};

const email_pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validate_email = (value: string): ResetErrors => {
  const errors: ResetErrors = {};
  if (!value.trim()) {
    errors.email = "Email is required.";
  } else if (!email_pattern.test(value)) {
    errors.email = "Enter a valid email.";
  }
  return errors;
};

export const ResetPassword: React.FC = () => {
  const [form_state, set_form_state] = useState<ResetFormState>(
    initial_form_state,
  );
  const [errors, set_errors] = useState<ResetErrors>({});
  const [status, set_status] = useState<"idle" | "submitting" | "success">(
    "idle",
  );

  const handle_field_change = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const { value } = event.target;
    set_form_state({ email: value });

    if (errors.email) {
      const field_errors = validate_email(value);
      set_errors(field_errors);
    }
  };

  const handle_field_blur = (
    event: React.FocusEvent<HTMLInputElement>,
  ): void => {
    const { value } = event.target;
    const field_errors = validate_email(value);

    if (Object.keys(field_errors).length > 0) {
      set_errors(field_errors);
      return;
    }

    set_errors({});
  };

  const handle_form_submit = async (
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();
    const field_errors = validate_email(form_state.email);

    if (Object.keys(field_errors).length > 0) {
      set_errors(field_errors);
      return;
    }

    set_errors({});
    set_status("submitting");

    try {
      await fetch(`${config.registration.baseUrl}/api/password-reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "omit",
        body: JSON.stringify({ email: form_state.email }),
      });

      set_status("success");
    } catch {
      set_status("success");
    }
  };

  if (status === "success") {
    return (
      <div className="reset-password-page">
        <div className="reset-password-splash">
          <Logo variant="dark" size="medium" />
          <h2>Check Your Email</h2>
          <p>
            If an account exists with that email, you will receive password reset
            instructions shortly.
          </p>
          <a href="/" className="reset-password-link">
            Return to Home
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-page">
      <div className="reset-password-container">
        <div className="reset-password-header">
          <Logo variant="dark" size="medium" />
          <h2>Reset Your Password</h2>
          <p>Enter your email to receive password reset instructions</p>
        </div>

        <Card className="reset-password-card" variant="bordered" padding="large">
          <form className="reset-password-form" onSubmit={handle_form_submit}>
            <div
              className={`reset-password-field ${
                errors.email ? "reset-password-field--error" : ""
              }`}
            >
              <label htmlFor="email">Email</label>
              <input
                id="email"
                name="email"
                type="email"
                required
                inputMode="email"
                placeholder="you@example.com"
                value={form_state.email}
                onChange={handle_field_change}
                onBlur={handle_field_blur}
                className={errors.email ? "reset-password-input--error" : undefined}
                aria-invalid={Boolean(errors.email)}
                disabled={status === "submitting"}
              />
              {errors.email && (
                <span className="reset-password-error">{errors.email}</span>
              )}
            </div>

            <Button
              size="large"
              variant="primary"
              type="submit"
              fullWidth
              loading={status === "submitting"}
              disabled={status === "submitting"}
            >
              {status === "submitting" ? "Sending..." : "Send Reset Link"}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
